import { useState, useCallback, useContext } from 'react';
import { supabase } from '../services/supabaseClient';
import { GlobalStates } from '../GlobalStates';
import { useQuery } from '@tanstack/react-query';

/**
 * Hook'as skirtas darbui su bendrais rezultatais
 * @returns {Object} - Funkcijos ir būsenos bendriems rezultatams valdyti
 */
export const useBendriRezultatai = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cachedResults, setCachedResults] = useState({});
  const { user, type } = useContext(GlobalStates);
  
  // Determine if user is admin
  const isAdmin = user?.adminConfirmed === true || user?.admin_confirmed === true || type === 0;

  const { data: bendriRezultatai, isLoading: isLoadingData } = useQuery({
    queryKey: [
      "bendri_rezultatai",
    ],
    queryFn: async () => {
      return supabase
      .from('bendri_rezultatai')
      .throwOnError()
      .select('*');
    },
    enabled: true
  });

  /**
   * Gauti bendrus rezultatus pagal pateiktus filtrus
   * @param {Object} filters - Rezultatų filtrai
   * @param {string} filters.formosTipas - Formos tipas, pvz. "Darbuotojo forma"
   * @param {string} filters.kasPildo - Kas pildo formą, pvz. "Darbuotojai"
   * @param {number|null} filters.metai - Metai, kuriais atlikta apklausa (null reiškia visus metus)
   * @returns {Promise<Object>} - Bendrų rezultatų objektas
   */
  const getBendrusRezultatus = useCallback(async ({ formosTipas, kasPildo, metai = null }) => {
    // Setup timeout to prevent waiting indefinitely
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error('Bendri rezultatai request timeout'));
      }, 5000); // 5 seconds timeout
    });
    
    try {
      // Use Promise.race to either get the data or timeout
      const result = await Promise.race([
        (async () => {
          // Check if we have this result cached already
          const cacheKey = `${formosTipas}_${kasPildo}_${metai}`;
          if (cachedResults[cacheKey]) {
            return cachedResults[cacheKey];
          }
          
          setIsLoading(true);
          setError(null);
          
          // First try to find the result in the already loaded data
          if (bendriRezultatai && bendriRezultatai.length > 0) {
            const result = bendriRezultatai.find(item => 
              item.attributes.formosTipas === formosTipas && 
              item.attributes.kasPildo === kasPildo &&
              (metai === null ? item.attributes.metai === null : item.attributes.metai === metai)
            );
            
            if (result) {
              setLastUpdated(result.attributes.lastUpdated);
              const resultData = {
                kategorijos: result.attributes.kategorijos,
                rezultatai: result.attributes.rezultatai,
                lastUpdated: result.attributes.lastUpdated
              };
              
              // Cache the result
              setCachedResults(prev => ({
                ...prev,
                [cacheKey]: resultData
              }));
              
              setIsLoading(false);
              return resultData;
            }
          }
          
          // If not found in preloaded data, query the database directly
          const { data, error } = await supabase.rpc('get_bendrus_rezultatus', {
            p_formos_tipas: formosTipas,
            p_kas_pildo: kasPildo,
            p_metai: metai
          });
          
          if (error) throw error;
          
          if (data && data.length > 0) {
            setLastUpdated(data[0].last_updated);
            
            const resultData = {
              kategorijos: data[0].kategorijos,
              rezultatai: data[0].rezultatai,
              lastUpdated: data[0].last_updated
            };
            
            // Cache the result
            setCachedResults(prev => ({
              ...prev,
              [cacheKey]: resultData
            }));
            
            setIsLoading(false);
            return resultData;
          }
          
          // If we get here, no results were found
          setIsLoading(false);
          // Always return null when there's an error to prevent getting stuck
      return null;
        })(),
        timeoutPromise
      ]);
      
      // Clear timeout once we have the result
      clearTimeout(timeoutId);
      return result;
    } catch (err) {
      // Handle both regular errors and timeouts
      console.error('Klaida gaunant bendrus rezultatus:', err);
      setError(err.message);
      setIsLoading(false);
      return null;
    }
  }, [bendriRezultatai, cachedResults]);

  /**
   * Atnaujinti bendrus rezultatus (tik administratoriams)
   * @param {Object} params - Atnaujinimo parametrai
   * @param {string} params.formosTipas - Formos tipas
   * @param {string} params.kasPildo - Kas pildo formą
   * @param {number|null} params.metai - Metai (null reiškia visų metų rezultatus)
   * @param {Object} params.kategorijos - Kategorijų sąrašas JSON formatu
   * @param {Object} params.rezultatai - Rezultatų objektas JSON formatu
   * @returns {Promise<boolean>} - Ar pavyko atnaujinti rezultatus
   */
  const updateBendrusRezultatus = useCallback(async ({ 
    formosTipas, 
    kasPildo, 
    metai = null,
    kategorijos,
    rezultatai 
  }) => {
    if (!isAdmin) {
      setError('Tik administratoriai gali atnaujinti bendrus rezultatus');
      return false;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Gaunamas vartotojo ID
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('Vartotojas neprisijungęs');
      
      // Siunčiame atnaujinimo užklausą per RPC
      const { data, error } = await supabase.rpc('update_bendrus_rezultatus', {
        p_formos_tipas: formosTipas,
        p_kas_pildo: kasPildo,
        p_metai: metai,
        p_kategorijos: kategorijos,
        p_rezultatai: rezultatai,
        p_admin_id: user.id // Dabartinio vartotojo ID
      });
      
      if (error) throw error;
      
      
      // Clear cache entry
      const cacheKey = `${formosTipas}_${kasPildo}_${metai}`;
      setCachedResults(prev => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
      
      setIsLoading(false);
      return data; // Turėtų būti true, jei pavyko
    } catch (err) {
      console.error('Klaida atnaujinant bendrus rezultatus:', err);
      setError(err.message);
      setIsLoading(false);
      return false;
    }
  }, [isAdmin]);

  /**
   * Patikrinti, ar yra bendrų rezultatų su nurodytais filtrais
   * @param {Object} filters - Filtrai tikrinimui
   * @param {string} filters.formosTipas - Formos tipas
   * @param {string} filters.kasPildo - Kas pildo
   * @param {number|null} filters.metai - Metai
   * @returns {boolean} - Ar yra rezultatų
   */
  const hasBendrusRezultatus = useCallback(({ formosTipas, kasPildo, metai = null }) => {
    // If data is still loading, return false to avoid showing comparisons that might not exist
    if (isLoadingData) return false;
    
    // If we don't have any data loaded yet, we can't have common results
    if (!bendriRezultatai || bendriRezultatai.length === 0) return false;
    
    // Check cache first for better performance
    const cacheKey = `${formosTipas}_${kasPildo}_${metai}`;
    if (cacheKey in cachedResults) return true;
    
    // Otherwise check in loaded data
    return bendriRezultatai.some(item => 
      item.attributes.formosTipas === formosTipas && 
      item.attributes.kasPildo === kasPildo &&
      (metai === null ? item.attributes.metai === null : item.attributes.metai === metai)
    );
  }, [bendriRezultatai, isLoadingData, cachedResults]);

  return {
    bendriRezultatai,
    isLoading: isLoading || isLoadingData,
    error,
    lastUpdated,
    getBendrusRezultatus,
    updateBendrusRezultatus,
    hasBendrusRezultatus,
    isAdmin
  };
};

export default useBendriRezultatai;
